import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate, STAGES } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Briefcase, Building2, Search, X } from "lucide-react";
import DealEditDialog from "@/components/DealEditDialog";

const COLOR_BY_STAGE = {
  "Webinar / Introduction": "border-slate-200",
  "Commercial Proposal": "border-blue-300",
  "Proof of Concept": "border-blue-500",
  "Commercial Approval": "border-amber-400",
  "Awaiting PO": "border-emerald-400",
  "Closed Won": "border-emerald-600",
  "Closed Lost": "border-slate-300",
};

// Fiscal year starts in April (Q1 Apr-Jun, Q2 Jul-Sep, Q3 Oct-Dec, Q4 Jan-Mar).
function periodRange(period, now = new Date()) {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth(); // 0=Jan
  const lastDay = (yy, mm) => new Date(Date.UTC(yy, mm + 1, 0)).getUTCDate();
  const iso = (yy, mm, dd) => `${yy}-${String(mm + 1).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  if (period === "all") return { start: null, end: null, label: "All open deals" };
  if (period === "current_month") {
    return {
      start: iso(y, m, 1),
      end: iso(y, m, lastDay(y, m)),
      label: new Date(Date.UTC(y, m, 1)).toLocaleString("en-GB", { month: "long", year: "numeric" }),
    };
  }
  if (period === "next_month") {
    const yy = y + Math.floor((m + 1) / 12);
    const mm = (m + 1) % 12;
    return {
      start: iso(yy, mm, 1),
      end: iso(yy, mm, lastDay(yy, mm)),
      label: new Date(Date.UTC(yy, mm, 1)).toLocaleString("en-GB", { month: "long", year: "numeric" }),
    };
  }
  // Fiscal quarter logic
  const fqIndex = Math.floor(((m + 12 - 3) % 12) / 3); // Apr→0
  const fyStartYear = m >= 3 ? y : y - 1;
  const offset = period === "current_quarter" ? 0 : 1;
  const target = fqIndex + offset;
  const fqTarget = target % 4;
  const fyTargetStartYear = fyStartYear + Math.floor(target / 4);
  let startMonth = 3 + fqTarget * 3; // April=3
  let startYear = fyTargetStartYear;
  if (startMonth > 11) {
    startMonth -= 12;
    startYear += 1;
  }
  let endMonth = startMonth + 2;
  let endYear = startYear;
  if (endMonth > 11) {
    endMonth -= 12;
    endYear += 1;
  }
  const qNum = fqTarget + 1;
  const fyEndYear = fyTargetStartYear + 1;
  const label = `FY${String(fyEndYear).slice(-2)} Q${qNum} (${new Date(Date.UTC(startYear, startMonth, 1)).toLocaleString("en-GB", { month: "short" })}–${new Date(Date.UTC(endYear, endMonth, 1)).toLocaleString("en-GB", { month: "short", year: "numeric" })})`;
  return {
    start: iso(startYear, startMonth, 1),
    end: iso(endYear, endMonth, lastDay(endYear, endMonth)),
    label,
  };
}

const PERIODS = [
  ["all", "All"],
  ["current_month", "Current month"],
  ["next_month", "Next month"],
  ["current_quarter", "Current quarter"],
  ["next_quarter", "Next quarter"],
];

export default function Deals() {
  const [allDeals, setAllDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const nav = useNavigate();

  const reload = async () => {
    const r = await api.get("/deals");
    setAllDeals(r.data);
  };

  useEffect(() => {
    (async () => {
      await reload();
      setLoading(false);
    })();
  }, []);

  const range = useMemo(() => periodRange(period), [period]);

  // Distinct owner list derived from all (non-renewal) deals so managers can
  // slice pipeline by rep in one click.
  const owners = useMemo(() => {
    const set = new Set();
    for (const d of allDeals) {
      if ((d.subscription_type || "").toLowerCase() === "renewal") continue;
      if (d.owner_name) set.add(d.owner_name);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allDeals]);

  // Pipeline excludes renewals — those live on the Renewals page.
  const deals = useMemo(() => {
    let base = allDeals.filter((d) => (d.subscription_type || "").toLowerCase() !== "renewal");
    if (ownerFilter !== "all") base = base.filter((d) => d.owner_name === ownerFilter);
    if (range.start) {
      base = base.filter((d) => d.close_date && d.close_date.slice(0, 10) >= range.start && d.close_date.slice(0, 10) <= range.end);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      base = base.filter((d) =>
        (d.name || "").toLowerCase().includes(q) ||
        (d.account_name || "").toLowerCase().includes(q) ||
        (d.primary_contact_name || "").toLowerCase().includes(q)
      );
    }
    return base;
  }, [allDeals, range, ownerFilter, search]);

  const totals = STAGES.map((stage) => {
    const list = deals.filter((d) => d.stage === stage);
    return { stage, list, sum: list.reduce((a, d) => a + (d.revenue || 0), 0) };
  });

  const openDeals = deals.filter((d) => !["Closed Won", "Closed Lost"].includes(d.stage));
  const totalPipeline = openDeals.reduce((a, d) => a + (d.revenue || 0), 0);
  const weighted = openDeals.reduce((a, d) => a + (d.forecast_value || 0), 0);
  const openMrr = openDeals.reduce((a, d) => a + (d.mrr || 0), 0);
  const wonMrr = deals.filter((d) => d.stage === "Closed Won").reduce((a, d) => a + (d.mrr || 0), 0);

  return (
    <div className="space-y-6" data-testid="deals-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Pipeline</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Deals</h1>
          <p className="text-slate-500 text-sm mt-1">
            Pipeline excluding renewals · Fiscal year starts April
            <span className="ml-1 text-slate-700 font-medium">· {range.label}</span>
            {ownerFilter !== "all" && (
              <span className="ml-1 text-slate-700 font-medium">· {ownerFilter}</span>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center" data-testid="deals-filters">
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              data-testid="deals-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, company…"
              className="bg-white border border-slate-200 rounded-md pl-8 pr-7 py-1.5 text-xs text-slate-700 w-56 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {search && (
              <button
                type="button"
                data-testid="deals-search-clear"
                onClick={() => setSearch("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <select
            data-testid="deals-owner-filter"
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            className="bg-white border border-slate-200 rounded-md px-3 py-1.5 text-xs font-medium text-slate-700 cursor-pointer hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All owners</option>
            {owners.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
          <div className="flex gap-1 flex-wrap" data-testid="deals-periods">
            {PERIODS.map(([v, l]) => (
              <button
                key={v}
                data-testid={`deals-period-${v}`}
                onClick={() => setPeriod(v)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                  period === v
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-700"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-5 p-5">
          <div data-testid="deals-stat-open-pipeline">
            <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Open pipeline</div>
            <div className="font-heading text-2xl font-bold mt-1">{fmtMoney(totalPipeline)}</div>
            <div className="text-xs text-slate-500 mt-1">{openDeals.length} open deals</div>
          </div>
          <div data-testid="deals-stat-open-mrr">
            <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Open MRR</div>
            <div className="font-heading text-2xl font-bold text-blue-700 mt-1">{fmtMoney(openMrr)}</div>
            <div className="text-xs text-slate-500 mt-1">From open opportunities</div>
          </div>
          <div data-testid="deals-stat-weighted">
            <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Weighted forecast</div>
            <div className="font-heading text-2xl font-bold text-emerald-700 mt-1">{fmtMoney(weighted)}</div>
            <div className="text-xs text-slate-500 mt-1">MRR × probability</div>
          </div>
          <div data-testid="deals-stat-won-mrr">
            <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Won MRR</div>
            <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{fmtMoney(wonMrr)}</div>
            <div className="text-xs text-slate-500 mt-1">Closed Won in window</div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-slate-500 font-mono text-sm">Loading…</div>
      ) : deals.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <Briefcase className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading text-lg text-slate-700">
            {search ? "No deals match your search" : "No deals in this window"}
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {search ? "Try a different term or clear the search." : "Try a different period or open an account to create a deal."}
          </div>
        </Card>
      ) : (
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-4 min-w-max" data-testid="kanban-board">
            {totals.map(({ stage, list, sum }) => (
              <div key={stage} className="w-72 flex-shrink-0">
                <div className={`rounded-md bg-slate-50 border-t-4 ${COLOR_BY_STAGE[stage] || "border-slate-200"} border-x border-b border-slate-200`}>
                  <div className="p-3 border-b border-slate-200 flex justify-between items-center">
                    <div className="font-heading text-sm font-bold text-slate-900">{stage}</div>
                    <div className="text-[10px] font-mono text-slate-500 uppercase">{list.length} · {fmtMoney(sum)}</div>
                  </div>
                  <div className="p-2 space-y-2 min-h-[100px]">
                    {list.map((d) => (
                      <Card
                        key={d.id}
                        onClick={() => setEditing(d)}
                        className="p-3 border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                        data-testid={`kanban-card-${d.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <button
                            onClick={(e) => { e.stopPropagation(); nav(`/app/accounts/${d.account_id}`); }}
                            className="text-xs text-slate-500 truncate flex items-center gap-1 hover:text-blue-600 hover:underline"
                            title="Open account"
                          >
                            <Building2 className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{d.account_name}</span>
                          </button>
                          {d.is_auto_renewal && (
                            <span className="text-[9px] font-mono uppercase tracking-wider px-1 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                              Renewal
                            </span>
                          )}
                        </div>
                        <div className="font-medium text-slate-900 text-sm truncate mt-0.5">{d.name}</div>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="font-mono text-sm text-slate-900">{fmtMoney(d.revenue)}</span>
                          <span className="text-[10px] text-slate-500 font-mono">{d.probability}%</span>
                        </div>
                        <div className="mt-1 flex items-center justify-between">
                          <span className="text-[10px] text-emerald-700 font-mono">MRR {fmtMoney(d.mrr)}</span>
                          <span className="text-[10px] text-blue-700 font-mono">forecast {fmtMoney(d.forecast_value)}</span>
                        </div>
                        <div className="mt-1 text-[10px] text-slate-400">{fmtDate(d.close_date)} · {d.term_months || 12}m</div>
                      </Card>
                    ))}
                    {list.length === 0 && (
                      <div className="text-xs text-slate-400 text-center py-4">No deals</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <DealEditDialog
          deal={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { await reload(); setEditing(null); }}
        />
      )}
    </div>
  );
}
